import React, { useMemo, useState } from "react";

import { layerLabel } from "../terrain3d/layerSpecs";
import { groupByTheme, searchEntries } from "./layerSearch";

/**
 * EXPLORATEUR — tout ce que le territoire sait montrer, en une fois.
 *
 * CE QUI MANQUAIT. Le composer ne proposait que deux façons d'accéder aux
 * données : chercher (encore faut-il savoir quoi) ou piocher dans une rangée
 * de couches. Impossible de simplement PARCOURIR le catalogue — donc
 * impossible de découvrir qu'un indicateur existe si on ne le cherchait pas.
 * Sur cinquante indicateurs, ça revient à en cacher la moitié.
 *
 * LE RANGEMENT EST THÉMATIQUE, PAS PAR FICHIER. « Communes », « Géologie »,
 * « Climat » disent où la donnée a été lue, pas de quoi elle parle : la
 * vulnérabilité des nappes se trouvait rangée avec la surface irriguée parce
 * qu'elles viennent du même export, loin des unités aquifères dont elle est
 * pourtant le prolongement. Les thèmes — grand cycle de l'eau, changement
 * climatique, risques et réglementation, usages — sont les rubriques d'un
 * diagnostic de ressource, et sont déclarés par le manifeste
 * (build_manifest.py) plutôt qu'écrits ici.
 *
 * IL SERT AUSSI À CHANGER un indicateur : restreint à une couche, il remplace
 * la liste déroulante à plat que celle-ci exposait. Un seul rangement à
 * apprendre, pour ajouter comme pour modifier.
 */
export default function LayerBrowser({
  entries, themes, activeLayers, layerSettings, onPick, onClose,
  restrictToLayer = null, restrictLabel = null,
}) {
  const [query, setQuery] = useState("");
  const [activeTheme, setActiveTheme] = useState(null);

  // Restreint à une couche, l'explorateur sert à CHANGER ce qu'elle affiche :
  // seuls ses indicateurs ont un sens, pas la couche elle-même ni les autres.
  const scoped = useMemo(
    () => (restrictToLayer
      ? entries.filter((e) => e.layerId === restrictToLayer && e.kind === "indicator")
      : entries),
    [entries, restrictToLayer]
  );

  const themed = useMemo(() => groupByTheme(scoped, themes), [scoped, themes]);
  const results = useMemo(
    () => (query.trim() ? searchEntries(scoped, query, { limit: 60 }) : null),
    [scoped, query]
  );

  // Une entrée déjà dans la vue reste visible mais marquée : la retirer de la
  // liste ferait croire qu'elle n'existe pas.
  const isActive = (entry) => {
    if (!activeLayers.includes(entry.layerId)) return false;
    if (entry.kind === "layer") return true;
    const settings = layerSettings[entry.layerId] || {};
    return Object.values(settings).includes(entry.indicatorId);
  };

  const sections = results
    ? [{ id: "results", label: `${results.length} résultat${results.length > 1 ? "s" : ""}`, entries: results }]
    : themed.filter((theme) => !activeTheme || theme.id === activeTheme);

  return (
    <div className="builder-picker-overlay" onClick={onClose}>
      <div className="layer-browser" onClick={(e) => e.stopPropagation()}>
        <div className="builder-picker-header">
          <h3>
            {restrictLabel
              ? `Que montrer dans « ${restrictLabel} » ?`
              : "Toutes les couches et indicateurs"}
          </h3>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="layer-browser-search">
          <span className="layer-browser-search-icon" aria-hidden="true">⌕</span>
          <input
            autoFocus
            type="search"
            className="layer-browser-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={restrictLabel ? `Chercher dans ${restrictLabel}…` : "Sécheresse, nappe, irrigation, étiage…"}
            aria-label="Chercher dans le catalogue"
          />
        </div>

        {/* Filtres de thème : ils servent à réduire, pas à naviguer — la liste
            reste entièrement parcourable au défilement. */}
        {!results && (
          <div className="layer-browser-themes">
            <button
              type="button"
              className={`layer-browser-theme${activeTheme === null ? " is-active" : ""}`}
              onClick={() => setActiveTheme(null)}
            >
              Tout
            </button>
            {themed.map((theme) => (
              <button
                key={theme.id}
                type="button"
                className={`layer-browser-theme${activeTheme === theme.id ? " is-active" : ""}`}
                onClick={() => setActiveTheme(activeTheme === theme.id ? null : theme.id)}
              >
                {theme.label}
                <span className="layer-browser-theme-count">{theme.entries.length}</span>
              </button>
            ))}
            {/* Un thème annoncé mais encore sans donnée est montré, désactivé :
                dire que le sujet est prévu vaut mieux que laisser croire qu'il
                n'existe pas dans l'outil. */}
            {!restrictToLayer && (themes || [])
              .filter((theme) => theme.upcoming && !themed.some((t) => t.id === theme.id))
              .map((theme) => (
                <span key={theme.id} className="layer-browser-theme is-upcoming" title={theme.description}>
                  {theme.label}
                  <span className="layer-browser-theme-count">à venir</span>
                </span>
              ))}
          </div>
        )}

        <div className="layer-browser-body">
          {sections.length === 0 && (
            <div className="builder-empty">Rien ne correspond à cette recherche.</div>
          )}

          {sections.map((section) => (
            <section key={section.id} className="layer-browser-section">
              <header className="layer-browser-section-head">
                <h4>{section.label}</h4>
                {section.description && <p>{section.description}</p>}
              </header>
              <div className="layer-browser-list">
                {section.entries.map((entry) => {
                  const active = isActive(entry);
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      className={`layer-browser-entry${active ? " is-active" : ""}`}
                      onClick={() => onPick(entry)}
                    >
                      <span className={`layer-browser-kind is-${entry.kind}`}>
                        {entry.kind === "layer" ? "Couche" : "Indicateur"}
                      </span>
                      <span className="layer-browser-entry-body">
                        <span className="layer-browser-entry-label">{entry.label}</span>
                        <span className="layer-browser-entry-detail">
                          {entry.kind === "layer"
                            ? entry.detail
                            : `${layerLabel(entry.layerId)}${entry.group ? ` · ${entry.group}` : ""}`}
                        </span>
                      </span>
                      <span className="layer-browser-entry-action">{active ? "Dans la vue" : "Ajouter"}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
